// src/utils/debouncedApiCalls.js
import { debounce } from 'lodash';

// Store debounced functions by field path
const debouncedCallsMap = {};

// Store latest values for each field
const latestValues = {};

// Store pending operations to prevent duplicates
const pendingOperations = {};

/**
 * Creates or returns a cached debounced function for API calls
 * This implementation ensures that the most recent value is always used
 * when the debounced function executes
 * 
 * @param {string} entityKey - Unique identifier for the entity (e.g., facility-123)
 * @param {string} fieldPath - Path of the field being updated (e.g., name, details.type)
 * @param {Function} apiCallFn - Function to call with the latest value
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} - Function that accepts a value and schedules a debounced update
 */
export const getDebouncedFieldUpdate = (entityKey, fieldPath, apiCallFn, delay = 800) => {
  const key = `${entityKey}:${fieldPath}`;
  
  // Create the debounced function if it doesn't exist
  if (!debouncedCallsMap[key]) {
    debouncedCallsMap[key] = debounce(() => {
      // When the debounced function executes, get the latest value
      const latestValue = latestValues[key];
      // Call the API with the latest value
      return apiCallFn(latestValue);
    }, delay);
  }
  
  // Return a function that updates the latest value and triggers the debounced call
  return (value) => {
    // Store the latest value
    latestValues[key] = value;
    // Trigger the debounced function
    return debouncedCallsMap[key]();
  };
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use getDebouncedFieldUpdate instead
 */
export const getDebouncedApiCall = (key, fn, delay = 800) => {
  if (!debouncedCallsMap[key]) {
    debouncedCallsMap[key] = debounce(fn, delay);
  }
  return debouncedCallsMap[key];
};

/**
 * Marks an operation as pending to prevent duplicates
 * 
 * @param {string} key - Unique operation key
 * @returns {boolean} - True if operation was newly marked, false if already pending
 */
export const markAsPending = (key) => {
  if (pendingOperations[key]) {
    return false;
  }
  pendingOperations[key] = true;
  return true;
};

/**
 * Clears pending status for an operation
 * 
 * @param {string} key - Unique operation key
 */
export const clearPending = (key) => {
  delete pendingOperations[key];
};

/**
 * Checks if an operation is pending
 * 
 * @param {string} key - Unique operation key
 * @returns {boolean} - True if operation is pending
 */
export const isPending = (key) => {
  return !!pendingOperations[key];
};

/**
 * Creates a safety timeout to clear pending status
 * in case an operation fails without properly clearing
 * 
 * @param {string} key - Unique operation key
 * @param {number} timeoutMs - Timeout in milliseconds
 */
export const createSafetyTimeout = (key, timeoutMs = 10000) => {
  setTimeout(() => {
    clearPending(key);
  }, timeoutMs);
};
